import { applyBounds, type LcdBounds } from './overlay';

const params = typeof location !== 'undefined'
  ? new URLSearchParams(location.search)
  : undefined;
const ENABLED = params?.has('borderCalibrate') === true || params?.get('debug') === '1';
const STORAGE_KEY = 'cybiko.lcdBorderBounds';
const MIN_SIZE = 0.05;
const MAX_SIZE = 1.5;
const MIN_OFFSET = -0.25;
const MAX_OFFSET = 1.25;

type ResizeHandle = 'n' | 'e' | 's' | 'w' | 'nw' | 'ne' | 'se' | 'sw';
type DragMode = 'move' | ResizeHandle;

interface DragState {
  readonly pointerId: number;
  readonly mode: DragMode;
  readonly startX: number;
  readonly startY: number;
  readonly startBounds: LcdBounds;
}

let current: LcdBounds | undefined;
let defaultBounds: LcdBounds | undefined;
let overlayEl: HTMLElement | undefined;
let boxEl: HTMLElement | undefined;
let readoutEl: HTMLOutputElement | undefined;
let exportEl: HTMLTextAreaElement | undefined;
let dragState: DragState | undefined;

export function applyStoredLcdBorderBounds(el: HTMLElement, fallback: LcdBounds): LcdBounds {
  const stored = loadBounds();
  current = stored ?? fallback;
  defaultBounds = fallback;
  overlayEl = el;
  applyBounds(el, current);
  return current;
}

export function initLcdBorderCalibration(frame: HTMLElement, el: HTMLElement): void {
  if (!ENABLED || !current) return;

  document.body.classList.add('lcd-border-calibration-mode');
  overlayEl = el;
  createBox(frame);
  createPanel();
  sync();
}

function createBox(frame: HTMLElement): void {
  boxEl = document.createElement('div');
  boxEl.className = 'lcd-border-calibration-box';
  boxEl.setAttribute('role', 'application');
  boxEl.setAttribute('aria-label', 'LCD border placement');

  for (const handle of ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const) {
    boxEl.appendChild(createHandle(handle));
  }

  boxEl.addEventListener('pointerdown', (event) => {
    const target = event.target instanceof HTMLElement ? event.target : undefined;
    const handle = target?.dataset.handle;
    beginDrag(event, isResizeHandle(handle) ? handle : event.shiftKey ? 'se' : 'move');
  });
  boxEl.addEventListener('pointermove', updateDrag);
  boxEl.addEventListener('pointerup', finishDrag);
  boxEl.addEventListener('pointercancel', finishDrag);

  frame.appendChild(boxEl);
}

function createHandle(handle: ResizeHandle): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = `lcd-border-calibration-handle lcd-border-calibration-handle--${handle}`;
  el.dataset.handle = handle;
  el.setAttribute('aria-label', `Stretch LCD border ${handle}`);
  el.addEventListener('pointerdown', (event) => beginDrag(event, handle));
  return el;
}

function createPanel(): void {
  const panel = document.createElement('section');
  panel.className = 'lcd-border-calibration-panel';
  panel.setAttribute('aria-label', 'LCD border calibration');

  const title = document.createElement('div');
  title.className = 'lcd-border-calibration-title';
  title.textContent = 'lcd border fit';

  const copyButton = makeButton('copy', () => {
    void copyExport();
  });
  const resetButton = makeButton('reset', reset);

  readoutEl = document.createElement('output');
  readoutEl.className = 'lcd-border-calibration-readout';

  exportEl = document.createElement('textarea');
  exportEl.className = 'lcd-border-calibration-export';
  exportEl.readOnly = true;
  exportEl.spellcheck = false;
  exportEl.rows = 5;
  exportEl.setAttribute('aria-label', 'LCD border bounds export');

  panel.append(title, copyButton, resetButton, readoutEl, exportEl);
  document.body.appendChild(panel);
}

function makeButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function beginDrag(event: PointerEvent, mode: DragMode): void {
  if (!boxEl || !current) return;
  stop(event);
  dragState = {
    pointerId: event.pointerId,
    mode,
    startX: event.clientX,
    startY: event.clientY,
    startBounds: current,
  };
  try {
    boxEl.setPointerCapture(event.pointerId);
  } catch {
    // Drag still works if pointer capture is unavailable.
  }
  window.addEventListener('pointermove', updateDrag, { capture: true });
  window.addEventListener('pointerup', finishDrag, { capture: true });
  window.addEventListener('pointercancel', finishDrag, { capture: true });
}

function updateDrag(event: PointerEvent): void {
  if (!dragState || !current || !boxEl) return;
  stop(event);
  const frame = boxEl.parentElement;
  if (!frame) return;
  const frameBox = frame.getBoundingClientRect();
  const dx = (event.clientX - dragState.startX) / frameBox.width;
  const dy = (event.clientY - dragState.startY) / frameBox.height;

  current = dragState.mode === 'move'
    ? moveBounds(dragState.startBounds, dx, dy)
    : resizeBounds(dragState.startBounds, dx, dy, dragState.mode);
  saveBounds(current);
  sync();
}

function finishDrag(event: PointerEvent): void {
  if (!dragState) return;
  stop(event);
  dragState = undefined;
  window.removeEventListener('pointermove', updateDrag, { capture: true });
  window.removeEventListener('pointerup', finishDrag, { capture: true });
  window.removeEventListener('pointercancel', finishDrag, { capture: true });
}

function moveBounds(bounds: LcdBounds, dx: number, dy: number): LcdBounds {
  return {
    ...bounds,
    top: clamp(bounds.top + dy, MIN_OFFSET, MAX_OFFSET),
    left: clamp(bounds.left + dx, MIN_OFFSET, MAX_OFFSET),
  };
}

function resizeBounds(bounds: LcdBounds, dx: number, dy: number, handle: ResizeHandle): LcdBounds {
  const right = bounds.left + bounds.width;
  const bottom = bounds.top + bounds.height;
  let top = bounds.top;
  let left = bounds.left;
  let width = bounds.width;
  let height = bounds.height;

  if (handle.includes('e')) width = bounds.width + dx;
  if (handle.includes('s')) height = bounds.height + dy;
  if (handle.includes('w')) {
    left = bounds.left + dx;
    width = right - left;
  }
  if (handle.includes('n')) {
    top = bounds.top + dy;
    height = bottom - top;
  }

  ({ start: left, size: width } = normalizeAxis(left, width, right, handle.includes('w')));
  ({ start: top, size: height } = normalizeAxis(top, height, bottom, handle.includes('n')));

  return { top, left, width, height };
}

function sync(): void {
  if (!current) return;
  if (overlayEl) applyBounds(overlayEl, current);
  if (boxEl) applyBounds(boxEl, current);
  updateReadout();
  updateExport();
}

function reset(): void {
  if (!defaultBounds) return;
  current = defaultBounds;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Export remains available if localStorage is unavailable.
  }
  sync();
}

async function copyExport(): Promise<void> {
  if (!exportEl) return;
  try {
    await navigator.clipboard.writeText(exportEl.value);
    setReadout('copied');
  } catch {
    exportEl.select();
    setReadout('copy manually');
  }
}

function updateReadout(): void {
  if (!current) return;
  setReadout(`left ${pct(current.left)} top ${pct(current.top)} width ${pct(current.width)} height ${pct(current.height)}`);
}

function setReadout(value: string): void {
  if (!readoutEl) return;
  readoutEl.value = value;
  readoutEl.textContent = value;
}

function updateExport(): void {
  if (!exportEl || !current) return;
  exportEl.value = JSON.stringify({
    lcd_window_overlay_bounds: formatBounds(current),
  }, null, 2);
}

function loadBounds(): LcdBounds | undefined {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isBounds(parsed)) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

function saveBounds(bounds: LcdBounds): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bounds));
  } catch {
    // Export text remains available if storage is unavailable.
  }
}

function isBounds(value: unknown): value is LcdBounds {
  if (!value || typeof value !== 'object') return false;
  const raw = value as Record<string, unknown>;
  return isFiniteNumber(raw.top)
    && isFiniteNumber(raw.left)
    && isFiniteNumber(raw.width)
    && isFiniteNumber(raw.height);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isResizeHandle(value: string | undefined): value is ResizeHandle {
  return value === 'n'
    || value === 'e'
    || value === 's'
    || value === 'w'
    || value === 'nw'
    || value === 'ne'
    || value === 'se'
    || value === 'sw';
}

function normalizeAxis(start: number, size: number, fixedEnd: number, movingStart: boolean): {
  readonly start: number;
  readonly size: number;
} {
  let nextStart = start;
  let nextSize = size;

  if (nextSize < MIN_SIZE) {
    nextSize = MIN_SIZE;
    if (movingStart) nextStart = fixedEnd - MIN_SIZE;
  }
  if (nextSize > MAX_SIZE) {
    nextSize = MAX_SIZE;
    if (movingStart) nextStart = fixedEnd - MAX_SIZE;
  }

  nextStart = clamp(nextStart, MIN_OFFSET, MAX_OFFSET);
  nextSize = clamp(nextSize, MIN_SIZE, MAX_SIZE);
  return { start: nextStart, size: nextSize };
}

function formatBounds(bounds: LcdBounds): Record<keyof LcdBounds, string> {
  return {
    top: pct(bounds.top),
    left: pct(bounds.left),
    width: pct(bounds.width),
    height: pct(bounds.height),
  };
}

function stop(event: PointerEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

function pct(value: number): string {
  return `${(value * 100).toFixed(3)}%`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
